import { CSSProperties } from "react";

import Image from "next/image";

import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Trans } from "@lingui/macro";
import { Button, Col, Divider, Input, Row } from "antd";

const DraggableChoice = ({
  choice,
  remove,
  onValueChange,
}: {
  choice: { id: string; value: string };
  remove: () => void;
  onValueChange: (val: string) => void;
}) => {
  const { attributes, isDragging, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
    id: choice.id,
    disabled: false,
  });

  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
    marginBottom: 8,
  };

  return (
    <Row ref={setNodeRef} style={style} align={"middle"}>
      <Col
        style={{ justifyContent: "center", alignItems: "center", display: "flex", cursor: "pointer" }}
        ref={setActivatorNodeRef}
        {...listeners}
        {...attributes}
      >
        <Image alt="drag-icon" src="/icons/drag.svg" width={12} height={16} />
      </Col>
      <Col style={{ paddingLeft: 8 }} flex={1}>
        <Input autoFocus onBlur={(e) => onValueChange(e.target.value)} defaultValue={choice.value} />
      </Col>
      <Col style={{ paddingLeft: 10, cursor: "pointer" }} onClick={() => remove()}>
        <DeleteOutlined />
      </Col>
    </Row>
  );
};

const ChoiceInput = ({
  choices,
  moveChoice,
  addChoice,
  removeChoice,
  updateChoiceValue,
}: {
  choices: { id: string; value: string }[];
  moveChoice: (value: any) => void;
  addChoice: () => void;
  removeChoice: (id: string) => void;
  updateChoiceValue: (id: string, value: string) => void;
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (!choices) {
    return null;
  }

  return (
    <Col>
      <Divider style={{ marginTop: 12, marginBottom: 16 }} />
      <DndContext
        sensors={sensors}
        onDragEnd={({ active, over }) => {
          if (over && active.id !== over?.id) {
            const activeIndex = choices.findIndex(({ id }) => id === active.id);
            const overIndex = choices.findIndex(({ id }) => id === over.id);

            moveChoice(arrayMove(choices, activeIndex, overIndex));
          }
        }}
      >
        <SortableContext items={choices}>
          {choices.map((c, i) => (
            <DraggableChoice
              onValueChange={(val: string) => updateChoiceValue(c.id, val)}
              remove={() => removeChoice(c.id)}
              key={`${c.id}-${i}`}
              choice={c}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button
        icon={
          <span style={{ marginRight: 8 }}>
            <PlusCircleOutlined />
          </span>
        }
        style={{ marginTop: 10 }}
        onClick={addChoice}
      >
        <Trans>Add choice</Trans>
      </Button>
    </Col>
  );
};

export default ChoiceInput;
